import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FolderInput } from "lucide-react";

const TARGETS = [["driver", "Driver"], ["vehicle", "Unit (truck / trailer)"], ["filing", "Filing"]];

export function FileToRecordButton({ attachment, customerId }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title="File to record" className="text-xs inline-flex items-center gap-1 opacity-70 hover:opacity-100 underline" data-testid={`file-to-record-${attachment.id}`}>
        <FolderInput className="h-3 w-3" />File to record
      </button>
      {open && <FileToRecordDialog attachment={attachment} customerId={customerId} onClose={() => setOpen(false)} />}
    </>
  );
}

export function FileToRecordDialog({ attachment, customerId, onClose }) {
  const [target, setTarget] = useState("driver");
  const [targetId, setTargetId] = useState("");
  const [docType, setDocType] = useState("Document");
  const [expiry, setExpiry] = useState("");
  const [options, setOptions] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setTargetId("");
    (async () => {
      try {
        if (target === "driver") {
          const { data } = await api.get("/drivers");
          setOptions(data.filter((d) => !customerId || d.customer_id === customerId).map((d) => ({ id: d.id, label: `${d.last_name}, ${d.first_name}` })));
        } else if (target === "vehicle") {
          const { data } = await api.get("/fleet/vehicles", { params: customerId ? { customer_id: customerId } : {} });
          setOptions(data.map((v) => ({ id: v.id, label: `Unit ${v.unit_number} · ${v.vehicle_type} ${v.make || ""} ${v.model || ""}`.trim() })));
        } else {
          const { data } = await api.get("/fleet/filings", { params: customerId ? { customer_id: customerId } : {} });
          setOptions(data.filter((f) => !customerId || f.customer_id === customerId).map((f) => ({ id: f.id, label: `${f.type_label || f.filing_type} · ${f.period} (${f.status.replace(/_/g, " ")})` })));
        }
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [target, customerId]);

  const submit = async () => {
    if (!targetId) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/messages/attachments/${attachment.id}/file-to`, { target, target_id: targetId, doc_type: docType || "Document", expiry_date: expiry || null });
      toast.success(`${attachment.file_name} filed to ${data.label}`); onClose();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="file-to-record-dialog">
        <DialogHeader><DialogTitle>File "{attachment.file_name}" to a record</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Record type</Label>
            <Select value={target} onValueChange={setTarget}><SelectTrigger data-testid="file-to-target-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TARGETS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          <div><Label className="text-xs">{TARGETS.find(([v]) => v === target)[1]}</Label>
            <Select value={targetId} onValueChange={setTargetId}><SelectTrigger data-testid="file-to-record-select"><SelectValue placeholder={options.length ? "Choose…" : "No records found"} /></SelectTrigger>
              <SelectContent className="max-h-64">{options.map((o) => <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Document type</Label><Input value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="file-to-doc-type" /></div>
            <div><Label className="text-xs">Expiry (optional)</Label><Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} data-testid="file-to-expiry" /></div>
          </div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy || !targetId} className="bg-blue-700 hover:bg-blue-800" data-testid="file-to-submit">{busy ? "Filing…" : "File to record"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
